/**
 * demoSnapshots.js — Pre-formatted demo data for each workspace (V108)
 *
 * Isolated static data — never mixed with real user data.
 * Sourced from the seeded ecosystem: Gasavant, Lafarge, Oko-Oba, Lagos PoA.
 * All values are realistic but clearly marked as demo data.
 */

export const DEMO_DATA = {

  // ── SME Demo Snapshot (based on Gasavant Africa Ltd) ─────────────────────
  sme: {
    org_name:    "Gasavant Africa Ltd (Demo)",
    sector:      "Energy Services",
    country:     "Nigeria",
    state:       "Lagos State",
    description: "This is example data showing a typical SME energy company tracking monthly emissions over 6 months.",
    periods: [
      { period_label: "2026-01", total_tco2e: 4.43, scope1: 1.98, scope2: 1.88, scope3: 0.57, is_locked: true, decision_label: "Proceed with Conditions" },
      { period_label: "2026-02", total_tco2e: 3.46, scope1: 1.62, scope2: 1.55, scope3: 0.29, is_locked: true, decision_label: "Proceed with Conditions" },
      { period_label: "2026-03", total_tco2e: 4.93, scope1: 2.21, scope2: 2.04, scope3: 0.68, is_locked: true, decision_label: "Ready for Management Review" },
      { period_label: "2026-04", total_tco2e: 3.83, scope1: 1.75, scope2: 1.73, scope3: 0.35, is_locked: false, decision_label: "Proceed with Conditions" },
      { period_label: "2026-05", total_tco2e: 4.11, scope1: 1.87, scope2: 1.86, scope3: 0.38, is_locked: false, decision_label: "Proceed with Conditions" },
      { period_label: "2026-06", total_tco2e: 3.72, scope1: 1.69, scope2: 1.71, scope3: 0.32, is_locked: false, decision_label: "Proceed with Conditions" },
    ],
    ytd_tco2e:  24.48,
    annual_target: 28.0,
    trajectory: "Stable Improving",
    portfolio_score: 68,
    confidence_avg: 71,
    financing_status: "Proceed with Conditions",
    key_insight: "Emissions declining over 6 months. 3 periods locked. Ready for external reporting with one more locked period.",
  },

  // ── Corporate Demo Snapshot (based on Lafarge Africa Plc) ─────────────────
  corporate: {
    org_name:    "Lafarge Africa Plc (Demo)",
    sector:      "Cement Manufacturing",
    country:     "Nigeria",
    state:       "Abuja FCT",
    description: "Example data for a large cement manufacturer tracking Scope 1/2/3 emissions under IFRS S2.",
    periods: [
      { period_label: "2026-01", total_tco2e: 6521.7, scope1: 5614.5, scope2: 365.7, scope3: 541.5, is_locked: true  },
      { period_label: "2026-02", total_tco2e: 5566.5, scope1: 4800.3, scope2: 352.1, scope3: 414.1, is_locked: true  },
      { period_label: "2026-03", total_tco2e: 7009.9, scope1: 5987.3, scope2: 383.4, scope3: 639.2, is_locked: true  },
      { period_label: "2026-04", total_tco2e: 6142.8, scope1: 5312.5, scope2: 370.2, scope3: 460.1, is_locked: false },
      { period_label: "2026-05", total_tco2e: 6380.4, scope1: 5478.7, scope2: 376.9, scope3: 524.8, is_locked: false },
    ],
    intensity: { tco2e_per_fte: 3.52, tco2e_per_1musd_revenue: 1487.0, tco2e_per_tonne_product: 1.10 },
    ytd_tco2e:    31621.3,
    annual_target: 75000.0,
    portfolio_score: 72,
    monetisation_opportunities: [
      { label: "Fuel Switching (Coal → Gas)", annual_reduction: 9431.0, pathway: "VCM", readiness: "Ready for Structuring", priority: "high" },
      { label: "Renewable Energy Installation", annual_reduction: 6521.0, pathway: "VCM", readiness: "Ready for Structuring", priority: "high" },
      { label: "Energy Efficiency (Kiln)",     annual_reduction: 4380.0, pathway: "Insetting", readiness: "Requires Improvement", priority: "medium" },
    ],
    key_insight: "7,000 tCO2e/month average. Fuel switching could reduce annual emissions by 12.5%. Institutional report generated.",
  },

  // ── Project Demo Snapshot (based on Oko-Oba Biogas) ──────────────────────
  project: {
    project_name: "Oko-Oba Biogas Recovery Project (Demo)",
    org_name:     "Oko-Oba Agro-Processing Ltd",
    sector:       "Waste / Biogas",
    state:        "Lagos State",
    project_type: "waste",
    stage:        "due_diligence",
    description:  "Example project: biogas recovery from agricultural waste, replacing diesel generators.",
    baseline_tco2e:       850.0,
    project_tco2e:        170.0,
    annual_reduction:     680.0,
    emissions_reduction_pct: 80.0,
    methodology:          "VCS AMS-III.AV — Landfill Gas Capture variant",
    carbon_pathway:       "VCM — Gold Standard co-benefit preferred",
    eligibility_score:    78,
    eligibility_outcome:  "Conditionally Eligible",
    carbon_revenue_range: { low: 6800, mid: 17000, high: 27200, currency: "USD", basis: "VCM standard pricing 2023" },
    key_insight: "80% emissions reduction vs baseline. FI eligibility score 78/100. Conditionally eligible. Additionality documentation incomplete.",
  },

  // ── FI Demo Snapshot ───────────────────────────────────────────────────────
  fi: {
    description: "Example FI pipeline showing 3 active deals across SME, Project, and Corporate workspaces.",
    deals: [
      {
        deal_id:      "FI-GASAVANT-2026-001",
        entity:       "Gasavant Africa Ltd (SME)",
        sector:       "Energy",
        status:       "due_diligence",
        eligibility:  "Conditionally Eligible (65/100)",
        carbon_pathway: "VCM Insetting",
        est_annual_carbon_revenue: "$1,200–$3,000",
        notes: "Clean energy SME. 6 months of tracked emissions. 3 locked periods.",
        stage_history: ["Screening", "Data Request", "Due Diligence →"],
      },
      {
        deal_id:      "FI-OKOBA-2026-002",
        entity:       "Oko-Oba Biogas Project",
        sector:       "Waste / Biogas",
        status:       "credit_committee",
        eligibility:  "Conditionally Eligible (78/100)",
        carbon_pathway: "VCM / Gold Standard",
        est_annual_carbon_revenue: "$6,800–$27,200",
        notes: "Biogas recovery project. 680 tCO2e/yr reduction. PDD preparation in progress.",
        stage_history: ["Screening", "Data Request", "Due Diligence", "Credit Committee →"],
      },
      {
        deal_id:      "FI-LAFARGE-2026-003",
        entity:       "Lafarge Africa Plc (Corporate)",
        sector:       "Cement",
        status:       "term_sheet",
        eligibility:  "Eligible (85/100)",
        carbon_pathway: "VCM (ACM0002 fuel switching)",
        est_annual_carbon_revenue: "$134,000–$340,000",
        notes: "Corporate transition finance. Fuel switching opportunity. Term sheet issued.",
        stage_history: ["Screening", "Due Diligence", "Credit Committee", "Term Sheet →"],
      },
    ],
    pipeline_summary: { total_deals: 3, total_capex_pipeline: "$4.2M", total_reduction_tco2e: "10,181 tCO2e/yr" },
    key_insight: "3 active deals across SME, Project, and Corporate. Combined potential: $140k–$370k carbon revenue/yr.",
  },

  // ── States Demo Snapshot (based on Lagos State) ───────────────────────────
  states: {
    state_name:   "Lagos State (Demo)",
    geography:    "Lagos State",
    iso_code:     "NG-LA",
    description:  "Example state workspace showing aggregated climate data and programme structuring for Lagos State.",
    aggregation: {
      org_count:     4,
      total_footprint_tco2e: 24.5,
      total_reduction_potential: 8500.0,
      projects:      2,
      sector_breakdown: [
        { sector: "energy", tco2e: 15.2, share_pct: 62 },
        { sector: "waste",  tco2e: 9.3,  share_pct: 38 },
      ],
    },
    programme: {
      ref_id:       "STATE-PROG-2026-013",
      name:         "Lagos State Clean Energy & Housing PoA 2026",
      type:         "poa",
      status:       "submitted",
      projects:     2,
      target_reduction: 8500.0,
      fi_deal:      "FI-STATE-LAGOS-2026-004",
    },
    article6_readiness: "Indicative Assessment — Government Actions Required",
    key_insight: "4 organizations aggregated. 1 programme created and submitted to FI pipeline. Cross-state validation active.",
  },
};

export const DEMO_ORG_NAMES = {
  sme:       "Gasavant Africa Ltd",
  corporate: "Lafarge Africa Plc",
  project:   "Oko-Oba Agro-Processing Ltd",
  fi:        "Keystone Bank",
  states:    "Lagos State Climate Authority",
};
